import React from 'react';

import { MergeFieldRegistryEntry } from '~forms/types';

interface ComponentProps {
  token: string;
  registry: MergeFieldRegistryEntry[];
}

/**
 * Renders a styled non-editable chip for an inline merge field token.
 * Looks up the registry label — never renders the raw token string.
 */
const MergeFieldToken: React.FC<ComponentProps> = ({ token, registry }) => {
  const entry = registry.find((r) => r.token === token);
  const displayLabel = entry?.label ?? token;

  return (
    <span className="merge-field-token" title={`Merge field: ${displayLabel}`} aria-label={`Merge field: ${displayLabel}`}>
      <span className="merge-field-token-icon">{'{'}</span>
      {displayLabel}
      <span className="merge-field-token-icon">{'}'}</span>
    </span>
  );
};

export default MergeFieldToken;

/**
 * Parses an InlineTokenString and returns an array of text/token segments.
 * Token format: {{merge:<token>}}
 */
export function parseInlineTokens(text: string): Array<{ type: 'text' | 'token'; value: string }> {
  const segments: Array<{ type: 'text' | 'token'; value: string }> = [];
  const regex = /\{\{merge:([^}]+)\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'token', value: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return segments;
}

interface InlineTokenTextProps {
  text: string;
  registry: MergeFieldRegistryEntry[];
}

/**
 * Renders text that may contain {{merge:token}} sentinels as text + MergeFieldToken chips.
 */
export const InlineTokenText: React.FC<InlineTokenTextProps> = ({ text, registry }) => {
  const segments = parseInlineTokens(text);
  return (
    <>
      {segments.map((seg, i) =>
        seg.type === 'text' ? (
          <span key={`${seg.type}-${i}-${seg.value}`}>{seg.value}</span>
        ) : (
          <MergeFieldToken key={`${seg.type}-${i}-${seg.value}`} token={seg.value} registry={registry} />
        ),
      )}
    </>
  );
};
